import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import realApiService from '../../../utils/realApiService';
import sessionService from '../../../utils/sessionService';

const MemberProfileModal = ({ isOpen, onClose, member }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && member) {
      loadMemberTasks();
    }
  }, [isOpen, member]);

  const loadMemberTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = member.user_id || member.id;
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await realApiService.cards.getByAssignedUser(userId);
      
      // Sort tasks: Overdue first, then by date, then no date
      const sortedTasks = (response || []).sort((a, b) => {
        const now = new Date();
        const dateA = a.due_date ? new Date(a.due_date) : null;
        const dateB = b.due_date ? new Date(b.due_date) : null;

        // Both have dates
        if (dateA && dateB) return dateA - dateB;
        // Only A has date
        if (dateA) return -1;
        // Only B has date
        if (dateB) return 1;
        // Neither has date
        return 0;
      });

      setTasks(sortedTasks);
    } catch (error) {
      console.error('Failed to load member tasks:', error);
      setError('Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !member) return null;

  const isOverdue = (task) => {
    if (task.status === 'completed' || !task.due_date) return false;
    const dueDate = new Date(task.due_date);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return dueDate < now;
  };

  const getStatusColor = (status, task) => {
    if (isOverdue(task)) return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
    
    const statusMap = {
      'todo': 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      'in_progress': 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
      'completed': 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    };
    return statusMap[status] || 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300';
  };

  const getPriorityColor = (priority) => {
    const priorityMap = {
      'high': 'text-red-600',
      'medium': 'text-amber-600',
      'low': 'text-blue-600',
      'urgent': 'text-red-700 font-bold',
    };
    return priorityMap[priority] || 'text-slate-600';
  };

  const handleTaskClick = (projectId) => {
    if (projectId) {
      navigate(`/project-management?id=${projectId}&tab=tasks`);
      onClose();
    }
  };

  const formatTimestamp = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    pending: tasks.filter(t => t.status !== 'completed').length,
    overdue: tasks.filter(t => isOverdue(t)).length,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-card border border-border rounded-xl shadow-focused w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted/30">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center border border-primary/20">
              <Icon name="User" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">{member.name}</h2>
              <p className="text-sm text-text-secondary">{member.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-muted"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Member Info & Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-1 space-y-4">
               <div className="p-4 bg-muted/20 rounded-lg border border-border">
                  <div className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1">Role</div>
                  <div className="text-sm font-medium text-text-primary capitalize">{member.role}</div>
               </div>
               <div className="p-4 bg-muted/20 rounded-lg border border-border">
                  <div className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1">Status</div>
                  <div className={`text-xs inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                    member.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                  }`}>
                    {member.status}
                  </div>
               </div>
            </div>

            <div className="md:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/10 text-center">
                  <div className="text-lg font-bold text-primary">{stats.total}</div>
                  <div className="text-xs text-text-secondary">Total Tasks</div>
                </div>
                <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/10 text-center">
                  <div className="text-lg font-bold text-green-600">{stats.completed}</div>
                  <div className="text-xs text-text-secondary">Completed</div>
                </div>
                <div className="p-4 bg-amber-500/5 rounded-lg border border-amber-500/10 text-center">
                  <div className="text-lg font-bold text-amber-600">{stats.pending}</div>
                  <div className="text-xs text-text-secondary">In Progress</div>
                </div>
                <div className="p-4 bg-red-500/5 rounded-lg border border-red-500/10 text-center">
                  <div className="text-lg font-bold text-red-600">{stats.overdue}</div>
                  <div className="text-xs text-text-secondary">Overdue</div>
                </div>
            </div>
          </div>

          {/* Tasks List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-text-primary flex items-center space-x-2">
                <Icon name="CheckSquare" size={20} className="text-primary" />
                <span>Assigned Tasks</span>
              </h3>
              {loading && <Icon name="Loader2" size={18} className="animate-spin text-primary" />}
            </div>

            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-center text-destructive text-sm font-medium">
                {error}
                <button onClick={loadMemberTasks} className="ml-2 underline hover:text-destructive/80">Try again</button>
              </div>
            )}

            <div className="space-y-3">
              {tasks.length > 0 ? (
                tasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => handleTaskClick(task.project?.id || task.projectId)}
                    className="group flex flex-col p-4 bg-muted/10 hover:bg-muted/30 border border-border hover:border-primary/30 rounded-lg transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-text-primary group-hover:text-primary transition-colors line-clamp-1">{task.title}</h4>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-tighter ${getStatusColor(task.status, task)}`}>
                        {isOverdue(task) ? 'OVERDUE' : task.status.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-text-secondary">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <Icon name="Folder" size={12} />
                          <span className="font-medium">{task.project?.name || 'General Project'}</span>
                        </span>
                        {task.priority && (
                          <span className={`flex items-center space-x-1 font-medium ${getPriorityColor(task.priority)}`}>
                            <Icon name="Flag" size={12} />
                            <span className="capitalize">{task.priority}</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} />
                        <span>Due {formatTimestamp(task.due_date)}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : !loading && !error ? (
                <div className="text-center py-12 bg-muted/10 rounded-lg border border-dashed border-border">
                  <div className="w-12 h-12 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="ClipboardList" size={24} className="text-text-secondary opacity-50" />
                  </div>
                  <p className="text-text-secondary font-medium">No tasks assigned to this member.</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/10 flex justify-end">
          <Button variant="outline" onClick={onClose} size="sm">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MemberProfileModal;
